import useAuth from 'hooks/useAuth/useAuth'
import { FetchingStatus } from 'hooks/useFetchApi/useFetchApi'
import {
    DefaultDeviceContract,
    PrinterContract,
    PaymentDeviceContract,
    HardwareAgentContract,
} from 'models'
import { useCallback, useEffect, useMemo } from 'react'
import { useDispatch } from 'react-redux'
import { getCurrentPosTerminalDevice } from 'store/actions/posTerminal.action'
import { useAppSelector } from 'store/useStoreHook'
import { POS_CFD_HWAGENT } from 'views/CustomerFacingDisplay/utils/localStorageKey'
const constructHardwareAgentUri = (
    hardwareAgent: HardwareAgentContract | undefined,
) => {
    if (hardwareAgent) {
        return `${hardwareAgent.schema}${hardwareAgent.host}:${hardwareAgent.port}`
    } else {
        return 'https://localhost:5800'
    }
}
const useDefaultDevice = (intiFetch: boolean = false) => {
    const dispatch = useDispatch()
    const { isAuthenticated } = useAuth()

    const fetchingStatus = useAppSelector(
        (state) => state.posTerminal.defaultDeviceLoading,
    )

    const currentPosTerminal:
        | DefaultDeviceContract
        | undefined = useAppSelector((state) => state.posTerminal.defaultDevice)

    const defaultPrinter: PrinterContract | undefined = useMemo(() => {
        return currentPosTerminal?.defaultReceiptPrinter
    }, [currentPosTerminal?.defaultReceiptPrinter])

    const defaultPaymentDevice:
        | PaymentDeviceContract
        | undefined = useMemo(() => {
        return currentPosTerminal?.defaultPaymentDevice
    }, [currentPosTerminal?.defaultPaymentDevice])

    const onScreenPrinter: PrinterContract | undefined = useMemo(() => {
        return currentPosTerminal?.onScreenPrinter
    }, [currentPosTerminal?.onScreenPrinter])

    const enableOnScreenPrinter: boolean = useMemo(() => {
        return (
            currentPosTerminal?.defaultReceiptPrinter?.printerID ===
            currentPosTerminal?.onScreenPrinter?.printerID
        )
    }, [
        currentPosTerminal?.defaultReceiptPrinter,
        currentPosTerminal?.onScreenPrinter,
    ])

    const enableHardwareAgentPrinting = useMemo(() => {
        return currentPosTerminal?.enableHardwareAgentPrinting
    }, [currentPosTerminal?.enableHardwareAgentPrinting])

    const enableAndroidPrinting = useMemo(() => {
        return currentPosTerminal?.enableAndroidPrinting
    }, [currentPosTerminal?.enableAndroidPrinting])

    const dispatchCurrentPosTerminalDevice = useCallback(() => {
        dispatch(getCurrentPosTerminalDevice())
    }, [dispatch])

    const printingHardwareAgentUri = useMemo(() => {
        return constructHardwareAgentUri(
            currentPosTerminal?.printingHardwareAgent,
        )
    }, [currentPosTerminal])

    const cfdHardwareAgentUri = useMemo(() => {
        const uri = constructHardwareAgentUri(
            currentPosTerminal?.cfdHardwareAgent,
        )
        localStorage.setItem(POS_CFD_HWAGENT, uri)
        return uri
    }, [currentPosTerminal])

    const enableCFDOnPosTerminal = useMemo(() => {
        return currentPosTerminal?.enableCustomerFacingDisplay
    }, [currentPosTerminal?.enableCustomerFacingDisplay])

    // TBD : Caller ID HW Agent Uri
    const callerIdHardwareAgentUri = useMemo(() => {
        return constructHardwareAgentUri(
            currentPosTerminal?.printingHardwareAgent,
        )
    }, [currentPosTerminal])

    useEffect(() => {
        if (
            intiFetch &&
            fetchingStatus === FetchingStatus.IDLE &&
            isAuthenticated
        ) {
            dispatchCurrentPosTerminalDevice()
        }
    }, [
        dispatchCurrentPosTerminalDevice,
        fetchingStatus,
        isAuthenticated,
        currentPosTerminal,
        intiFetch,
    ])

    return {
        defaultPrinter,
        defaultPaymentDevice,
        onScreenPrinter,
        loading: fetchingStatus,
        enableOnScreenPrinter,
        enableAndroidPrinting,
        enableHardwareAgentPrinting,
        currentPosTerminal,
        printingHardwareAgentUri,
        cfdHardwareAgentUri,
        dispatchCurrentPosTerminalDevice,
        enableCFDOnPosTerminal,
        callerIdHardwareAgentUri,
    }
}

export default useDefaultDevice
